import { defineStore } from 'pinia';
import { api, getStoredToken, setAuthToken } from '@/services/api';

export interface AdminUser {
  id: number;
  username: string;
  name: string | null;
  role: 'admin' | 'editor' | 'viewer';
  can_write: boolean;
  is_admin: boolean;
}

interface State {
  user: AdminUser | null;
  token: string | null;
  restored: boolean;
  loading: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    user: null,
    token: getStoredToken(),
    restored: false,
    loading: false,
  }),

  getters: {
    isAuthenticated: (s) => !!s.user,
    canWrite: (s) => !!s.user?.can_write,
  },

  actions: {
    async login(username: string, password: string, totp: string): Promise<void> {
      this.loading = true;
      try {
        const { data } = await api.post<{
          token: string;
          user: AdminUser;
        }>('/auth/login', { username, password, totp });
        this.token = data.token;
        this.user = data.user;
        setAuthToken(data.token);
      } finally {
        this.loading = false;
      }
    },

    async restoreSession(): Promise<void> {
      if (this.restored) return;
      if (!this.token) {
        this.restored = true;
        return;
      }
      try {
        const { data } = await api.get<{ user: AdminUser }>('/auth/me');
        this.user = data.user;
      } catch {
        this.token = null;
        this.user = null;
        setAuthToken(null);
      } finally {
        this.restored = true;
      }
    },

    async logout(): Promise<void> {
      try {
        await api.post('/auth/logout');
      } catch {
        // ignore
      }
      this.token = null;
      this.user = null;
      setAuthToken(null);
    },
  },
});
